import { CompanyRequestError, companyRequest } from '@/company/api';
import type { CompanyFile } from './types';
import { fileSha256 } from './upload-digest';
import { reconcileMultipartParts, type RetainedPart } from './multipart-resume';
import { workspaceErrorCopy } from './workspace-errors';

const root = '/api/v1/company/files/uploads';

type UploadSession = {
  id: string;
  name?: string;
  mimeType?: string;
  sizeBytes?: number;
  mode: 'single' | 'multipart';
  status: string;
  expectedSha256?: string | null;
  parts?: RetainedPart[];
  post?: { url: string; fields: Record<string, string> };
};

/** The connection dropped while the file was on its way: the upload can go on where it stopped. */
export class UploadConnectionLost extends Error {
  constructor(locale?: string) {
    super(workspaceErrorCopy(locale).uploadInterrupted);
    this.name = 'UploadConnectionLost';
  }
}

const cancelled = () => new DOMException('The upload was cancelled.', 'AbortError');

/** A Chat request on the way: fetch rejects with a TypeError only when the request never got an answer. */
function request<T>(...args: Parameters<typeof companyRequest>) {
  return companyRequest<T>(...args).catch((error: unknown) => {
    throw error instanceof TypeError ? new UploadConnectionLost() : error;
  });
}

/** One request with its body's progress; fetch reports no upload progress, XMLHttpRequest does. */
function transfer(
  method: 'PUT' | 'POST',
  url: string,
  body: XMLHttpRequestBodyInit,
  {
    headers = {},
    onProgress,
    signal,
  }: {
    headers?: Record<string, string>;
    onProgress?: (loaded: number, total: number) => void;
    signal?: AbortSignal;
  },
) {
  return new Promise<XMLHttpRequest>((resolve, reject) => {
    if (signal?.aborted) {
      reject(cancelled());
      return;
    }
    const request = new XMLHttpRequest();
    const stop = () => request.abort();
    const settle = (finish: () => void) => {
      signal?.removeEventListener('abort', stop);
      finish();
    };
    request.open(method, url);
    for (const [name, value] of Object.entries(headers)) request.setRequestHeader(name, value);
    request.onload = () => settle(() => resolve(request));
    request.onerror = () => settle(() => reject(new UploadConnectionLost()));
    request.onabort = () => settle(() => reject(cancelled()));
    if (onProgress) request.upload.onprogress = (event) => onProgress(event.loaded, event.total);
    signal?.addEventListener('abort', stop, { once: true });
    request.send(body);
  });
}

async function putPart(
  url: string,
  body: Blob,
  headers: Record<string, string>,
  onProgress?: (loaded: number) => void,
  signal?: AbortSignal,
  locale?: string,
) {
  const request = await transfer('PUT', url, body, {
    headers,
    onProgress: onProgress && ((loaded) => onProgress(loaded)),
    signal,
  });
  if (request.status < 200 || request.status >= 300)
    throw new Error(workspaceErrorCopy(locale).uploadPart);
  const etag = request.getResponseHeader('etag')?.replaceAll('"', '');
  if (!etag) throw new Error(workspaceErrorCopy(locale).uploadEtag);
  return {
    etag,
    ...(headers['x-amz-checksum-sha256']
      ? { checksumSha256: headers['x-amz-checksum-sha256'] }
      : {}),
  };
}

async function uploadSingle(
  session: UploadSession,
  file: File,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal,
  locale?: string,
) {
  if (!session.post) throw new Error(workspaceErrorCopy(locale).uploadGrant);
  const form = new FormData();
  for (const [name, value] of Object.entries(session.post.fields)) form.append(name, value);
  form.append('file', file);
  const refused = () => new Error(workspaceErrorCopy(locale).uploadRefused);
  if (typeof XMLHttpRequest === 'undefined') {
    // Without XMLHttpRequest the same form goes by fetch, with no progress until it lands.
    const response = await fetch(session.post.url, { method: 'POST', body: form, signal }).catch(
      () => {
        throw signal?.aborted ? cancelled() : new UploadConnectionLost();
      },
    );
    if (!response.ok) throw refused();
  } else {
    const request = await transfer('POST', session.post.url, form, {
      onProgress: onProgress && ((loaded, total) => total > 0 && onProgress(loaded / total)),
      signal,
    });
    if (request.status < 200 || request.status >= 300) throw refused();
  }
  onProgress?.(1);
  return [];
}

async function uploadMultipart(
  session: UploadSession,
  file: File,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal,
  locale?: string,
) {
  const { pending, retained, partBytes } = await reconcileMultipartParts(
    file,
    session.parts,
    locale,
  );
  // Parts are signed a batch at a time, just before they go: a grant lasts 15 minutes, and files uploading side by
  // side share the connection, so a large file's later parts could outlive grants signed at its start.
  const sign = async (batch: typeof pending) =>
    (
      await request<{
        parts: { partNumber: number; url: string; headers: Record<string, string> }[];
      }>(`${root}/${encodeURIComponent(session.id)}/parts`, 'POST', {
        parts: batch.map(({ partNumber, checksumSha256 }) => ({ partNumber, checksumSha256 })),
      })
    ).parts;
  const completed: { partNumber: number; etag: string; checksumSha256?: string }[] = [
    ...[...retained.values()].map(({ partNumber, etag, checksumSha256 }) => ({
      partNumber,
      etag,
      ...(checksumSha256 ? { checksumSha256 } : {}),
    })),
  ];
  let committedBytes = [...retained].reduce(
    (total, [partNumber, part]) =>
      total +
      (part.sizeBytes ??
        Math.min(partBytes, Math.max(0, file.size - (partNumber - 1) * partBytes))),
    0,
  );
  onProgress?.(committedBytes / file.size);
  // Three parts in flight keeps a large upload moving without holding the whole file in memory; files uploading
  // side by side share the browser's connections between them.
  for (let offset = 0; offset < pending.length; offset += 3) {
    if (signal?.aborted) throw cancelled();
    const batch = pending.slice(offset, offset + 3);
    const byNumber = new Map((await sign(batch)).map((part) => [part.partNumber, part]));
    if (signal?.aborted) throw cancelled();
    const inFlight = new Map<number, number>();
    // One part failing stops its siblings too: the session keeps what landed, and a resume sends the rest.
    const batchStop = new AbortController();
    const stop = () => batchStop.abort();
    signal?.addEventListener('abort', stop, { once: true });
    try {
      completed.push(
        ...(await Promise.all(
          batch.map(async ({ partNumber, body }) => {
            const part = byNumber.get(partNumber);
            if (!part) throw new Error(workspaceErrorCopy(locale).uploadParts);
            try {
              const result = await putPart(
                part.url,
                body,
                part.headers,
                (loaded) => {
                  inFlight.set(partNumber, loaded);
                  onProgress?.(
                    Math.min(
                      1,
                      (committedBytes + [...inFlight.values()].reduce((a, b) => a + b, 0)) /
                        file.size,
                    ),
                  );
                },
                batchStop.signal,
                locale,
              );
              return { partNumber, ...result, sizeBytes: body.size };
            } catch (error) {
              batchStop.abort();
              throw error;
            }
          }),
        )),
      );
    } finally {
      signal?.removeEventListener('abort', stop);
    }
    committedBytes += batch.reduce((total, part) => total + part.body.size, 0);
    onProgress?.(Math.min(1, committedBytes / file.size));
  }
  return completed
    .sort((a, b) => a.partNumber - b.partNumber)
    .map(({ partNumber, etag, checksumSha256 }) => ({
      partNumber,
      etag,
      ...(checksumSha256 ? { checksumSha256 } : {}),
    }));
}

function resumeKey(
  file: File,
  sha256: string,
  mimeType: string,
  purpose: string,
  includeInKnowledge: boolean,
) {
  return `poneva.upload.${sha256}:${file.name}:${mimeType}:${purpose}:${includeInKnowledge}`;
}

function resumeId(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remember(key: string, id: string | null) {
  try {
    if (id) localStorage.setItem(key, id);
    else localStorage.removeItem(key);
  } catch {}
}

export async function directUploadFile({
  file,
  mimeType,
  includeInKnowledge,
  purpose = 'library',
  onProgress,
  signal,
  onSession,
  locale,
}: {
  file: File;
  mimeType: string;
  includeInKnowledge: boolean;
  purpose?: 'library' | 'meeting';
  onProgress?: (progress: number) => void;
  /** Aborting cancels the transfer and discards its session; a completion already sent goes on. */
  signal?: AbortSignal;
  /**
   * Called once the server holds the session, with a way to discard it later (a paused or refused upload the
   * person removes), and with null once this call has discarded it itself or the server has ended it. The server
   * keeps a few active sessions per person, so a forgotten one holds one of them.
   */
  onSession?: (discard: (() => Promise<void>) | null) => void;
  locale?: string;
}) {
  if (signal?.aborted) throw cancelled();
  const sha256 = await fileSha256(file, locale);
  if (signal?.aborted) throw cancelled();
  const key = resumeKey(file, sha256, mimeType, purpose, includeInKnowledge);
  let upload: UploadSession | null = null;
  const previous = resumeId(key);
  if (previous) {
    try {
      const response = await request<{ upload: UploadSession }>(
        `${root}/${encodeURIComponent(previous)}`,
      );
      if (
        response.upload.name === file.name &&
        response.upload.sizeBytes === file.size &&
        response.upload.mimeType === mimeType &&
        response.upload.expectedSha256 === sha256 &&
        ((response.upload.status === 'uploading' &&
          (response.upload.mode === 'multipart' || Boolean(response.upload.post))) ||
          ['validating', 'promoting', 'complete'].includes(response.upload.status))
      )
        upload = response.upload;
    } catch (error) {
      // A payment refusal has not ended the upload: Retry once the plan is fixed goes on with it, never a new one.
      if (paymentRequired(error)) throw error;
    }
    if (!upload) remember(key, null);
  }
  if (!upload)
    ({ upload } = await request<{ upload: UploadSession }>(root, 'POST', {
      name: file.name,
      mimeType,
      sizeBytes: file.size,
      sha256,
      includeInKnowledge,
      purpose,
    }));
  // Keep the session identity until completion returns. A connection can be
  // lost after the server has promoted the object; the next attempt must
  // reconcile that same receipt instead of creating or aborting another one.
  remember(key, upload.id);
  const sessionId = upload.id;
  onSession?.(async () => {
    remember(key, null);
    await companyRequest(`${root}/${encodeURIComponent(sessionId)}`, 'DELETE').catch(() => {});
  });
  let parts: { partNumber: number; etag: string; checksumSha256?: string }[] = [];
  const transferring = !['validating', 'promoting', 'complete'].includes(upload.status);
  // The server already holds every byte of a session it is finishing: the row reads the file, not 0 of its size.
  if (!transferring) onProgress?.(1);
  try {
    if (signal?.aborted) throw cancelled();
    parts = !transferring
      ? []
      : upload.mode === 'single'
        ? await uploadSingle(upload, file, onProgress, signal, locale)
        : await uploadMultipart(upload, file, onProgress, signal, locale);
    if (signal?.aborted) throw cancelled();
  } catch (error) {
    // Only a byte-transfer failure is abortable. Once completion starts, an
    // uncertain response is reconciled through this same durable session. A
    // lost connection keeps a multipart session to resume; cancelling ends it.
    if (upload.mode === 'single' || (signal?.aborted && transferring)) {
      const discarded = await companyRequest(`${root}/${encodeURIComponent(upload.id)}`, 'DELETE')
        .then(() => true)
        .catch(() => false);
      remember(key, null);
      if (discarded) onSession?.(null);
    }
    throw error;
  }
  const completed = await request<{ session: UploadSession; file: CompanyFile }>(
    `${root}/${encodeURIComponent(upload.id)}/complete`,
    'POST',
    { parts, sha256 },
  ).catch(async (error: unknown) => {
    // A payment refusal is not an ended session: keep it as it is, for Retry once the plan is fixed.
    if (paymentRequired(error)) throw error;
    // A refusal (4xx) ends the session only when the server failed it (a checksum or verification refusal). One
    // refused while another completion is still running, after promotion or before the claim stays active, so ask
    // the server before forgetting it: a live session is finished by Retry, never uploaded a second time.
    if (error instanceof CompanyRequestError && error.status < 500) {
      const ended = await companyRequest(`${root}/${encodeURIComponent(sessionId)}`)
        .then(() => false)
        .catch(
          (lookup: unknown) =>
            lookup instanceof CompanyRequestError &&
            lookup.status < 500 &&
            !paymentRequired(lookup),
        );
      if (ended) {
        remember(key, null);
        onSession?.(null);
      }
    }
    throw error;
  });
  remember(key, null);
  return completed;
}

export const storageUnavailable = (error: unknown) =>
  error instanceof CompanyRequestError && error.code === 'workspace_storage_unavailable';

/** The company's plan, credits or the person's limit refused the work: nothing about the upload itself. */
export const paymentRequired = (error: unknown) =>
  error instanceof CompanyRequestError && error.status === 402 && error.code === 'payment_required';
